import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

// iter 186 (2026-02): Configure which columns appear in Excel / PDF exports.
// The list mirrors the fixed 9-column layout that both `exportQuotationExcel`
// and `exportQuotationPdf` render. `photo`, `item_no`, and `description` are
// treated as required — the export loses most of its meaning without them,
// so we hard-lock the checkbox on. All other fields default to on but can
// be toggled off. Preference persists in localStorage under EXPORT_FIELDS_KEY
// so the same setting applies to every future export in this browser.
// iter 221 (2026-02): 新增 4 个可选字段 gt_sku / order_qty / purchase_price /
// gt_price。默认关闭(避免把商业敏感的 RMB 采购价意外暴露给客户报价),
// 用户勾选后本次及后续导出都带上。
export const EXPORTABLE_FIELDS = [
  { id: "photo",          label: "Photo",              required: true },
  { id: "description",    label: "Description",        required: true },
  { id: "material",       label: "Material / Specification" },
  { id: "item_no",        label: "Item No",            required: true },
  { id: "gt_sku",         label: "GT SKU" },
  { id: "size",           label: "Size" },
  { id: "pcs_per_ctn",    label: "Pcs / CTN" },
  { id: "carton_dims",    label: "Carton L\u00d7W\u00d7H (cm)" },
  { id: "cbm",            label: "CBM / Carton" },
  { id: "qty_40ft",       label: "Qty / 40'HQ" },
  { id: "qty",            label: "QTY (CI Inventory, available)" },
  { id: "reserved_qty",   label: "Reserved QTY (held for this quotation)" },
  { id: "order_qty",      label: "Order QTY" },
  { id: "barcode",        label: "Barcode / UPC" },
  { id: "purchase_price", label: "Purchase Price (RMB) · 采购价" },
  { id: "gt_price",       label: "GT Price" },
  { id: "price",          label: "Price" },
];
// iter 189 (2026-02): key bumped to v2 when 3 new dimensional fields were
// added — resets any prefs saved during the ~24h v1 window so the new
// fields default ON instead of silently unchecked for early adopters.
// iter 221 (2026-02): v3 —— 新增 4 字段默认 OFF(不干扰老导出偏好);
// 老 v2 存储会被重置一次。
export const EXPORT_FIELDS_KEY = "cartExportFields.v3";
// iter 221: 4 个新字段默认不勾选,让老用户导出结构保持稳定。
const OPT_IN_FIELDS = new Set(["gt_sku", "order_qty", "purchase_price", "gt_price"]);
export const DEFAULT_EXPORT_FIELDS = EXPORTABLE_FIELDS
  .filter((f) => !OPT_IN_FIELDS.has(f.id))
  .map((f) => f.id);

export function loadExportFields() {
  try {
    const raw = localStorage.getItem(EXPORT_FIELDS_KEY);
    if (!raw) return DEFAULT_EXPORT_FIELDS;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_EXPORT_FIELDS;
    // iter 278: reserved_qty is new and default-on; add it once for selections saved before it existed.
    if (!arr.includes("reserved_qty") && !localStorage.getItem("exportFieldsHasReserved")) { arr.push("reserved_qty"); localStorage.setItem("exportFieldsHasReserved", "1"); }
    // Always force required fields on regardless of stored value.
    const required = EXPORTABLE_FIELDS.filter((f) => f.required).map((f) => f.id);
    return Array.from(new Set([...required, ...arr]));
  } catch {
    return DEFAULT_EXPORT_FIELDS;
  }
}

export default function ExportFieldsDialog({ open, onOpenChange, onChange }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(DEFAULT_EXPORT_FIELDS);

  useEffect(() => {
    if (open) setSelected(loadExportFields());
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    localStorage.setItem(EXPORT_FIELDS_KEY, JSON.stringify(selected));
    onChange?.(selected);
    onOpenChange(false);
  };

  const handleReset = () => setSelected(DEFAULT_EXPORT_FIELDS);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-md" data-testid="export-fields-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {t("cart.export_fields_settings") === "cart.export_fields_settings"
              ? "Export Fields"
              : t("cart.export_fields_settings")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500 -mt-1">
          {t("cart.export_fields_hint") === "cart.export_fields_hint"
            ? "Pick which columns appear in the exported Excel / PDF. Required columns are locked."
            : t("cart.export_fields_hint")}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {EXPORTABLE_FIELDS.map((f) => (
            <label
              key={f.id}
              data-testid={`export-field-${f.id}-row`}
              className={`flex items-center gap-2 h-8 px-2 rounded-md border ${
                selected.includes(f.id) ? "border-brand-300 bg-brand-50/40" : "border-gray-200"
              } ${f.required ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:border-brand-300"}`}
            >
              <Checkbox
                data-testid={`export-field-${f.id}-checkbox`}
                checked={selected.includes(f.id)}
                disabled={f.required}
                onCheckedChange={() => !f.required && toggle(f.id)}
              />
              <span className="text-xs text-gray-700">{f.label}</span>
              {f.required && (
                <span className="text-[9px] uppercase tracking-label text-gray-400 ml-auto">Req</span>
              )}
            </label>
          ))}
        </div>
        <DialogFooter className="mt-3 gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleReset}
            data-testid="export-fields-reset"
            className="text-xs h-8"
          >
            {t("common.reset")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            data-testid="export-fields-save"
            className="text-xs h-8 bg-brand-600 hover:bg-brand-700 text-white"
          >
            {t("common.save") === "common.save" ? "Save" : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
